// Screen density — how much fits on this device's screen (2026-09-27).
//
// The owner, on his laptops: on a 15.6" and on a smaller one everything looks
// „გაწელილი", while on a big monitor it is ideal. The app was drawn at a
// monitor's size — a 256 px sidebar, generous padding, big tiles — and on a
// laptop's 1366 × 768 (or 1536 × 864 at Windows' 125 %) that chrome eats the
// screen: the warehouse table lost its price and stock columns to the right
// edge, the till's names were cut to „რობოტი — წყალ…".
//
// The lever is ONE number, the root font size: Tailwind sizes padding, gaps,
// icons, panels and most type in rem, so 14 px instead of 16 px scales the
// whole chrome by 7/8. Three things keep it honest:
//   • the type floor is pinned in globals.css — `--text-xs` never goes under
//     12 px, the owner's floor (feedback: „no 11px secondary text"); the many
//     `text-[12px]`/`text-[13px]` labels are px and do not move at all;
//   • breakpoints do not move — a media query's rem is the browser's initial
//     size, not <html>'s — so no layout switches between phone and desktop;
//   • phones (< 768 px) are untouched: their screens were drawn at 16 px.
//
// `auto` is CSS only (globals.css → SCREEN DENSITY): 16 px from 1600 px wide,
// 15 px below that, 14 px below 1440 wide or 800 tall. A device can pin it —
// a 14" laptop at 100 % scaling reports 1920 px and looks „auto = big", so the
// owner chooses per device, like the palette.

import type { Key } from './i18n'

export type Density = 'auto' | 'compact' | 'comfortable'

export const DENSITIES: { key: Density; label: Key; hint: Key }[] = [
  { key: 'auto', label: 'density.auto', hint: 'density.auto.hint' },
  { key: 'compact', label: 'density.compact', hint: 'density.compact.hint' },
  { key: 'comfortable', label: 'density.comfortable', hint: 'density.comfortable.hint' },
]

/** localStorage key. ⚠️ DUPLICATED IN `index.html`'s boot script (it runs
 *  before any module); `lib/density.test.ts` asserts the two agree. */
export const DENSITY_STORAGE_KEY = 'density'

const isDensity = (v: unknown): v is Density => v === 'auto' || v === 'compact' || v === 'comfortable'

export function getStoredDensity(): Density {
  try {
    const v = localStorage.getItem(DENSITY_STORAGE_KEY)
    return isDensity(v) ? v : 'auto'
  } catch {
    return 'auto'
  }
}

/** `auto` leaves <html> without the attribute, so the CSS media rules decide. */
export function applyDensity(d: Density) {
  const html = document.documentElement
  if (d === 'auto') html.removeAttribute('data-density')
  else html.setAttribute('data-density', d)
  try { localStorage.setItem(DENSITY_STORAGE_KEY, d) } catch { /* private mode: this session only */ }
}
